import { attributionPayload, isTestSession, newMetaEventId, pixelTrack, rememberVisitor } from "../../lib/metaEvent";
import { checkEmail, phoneLooksValid } from "../../lib/emailCheck";
import { gaEvent } from "../../lib/ga";
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  X,
  ArrowLeft,
  ArrowRight,
  Lock,
  Home,
  RefreshCw,
  Zap,
  Compass,
  TrendingUp,
  Activity,
  ShieldCheck,
  Phone,
  CalendarCheck,
  Bell,
  CheckCircle2,
  FileText,
} from "lucide-react";
import { publicApi, fmtMoney, fmtRate } from "./api";
import { LenderResultCard } from "./LenderCard";
import { saveFunnelSession } from "./funnelSession";
import { getPartnerSlug } from "./partnerRef";

const STEPS = ["intent_l1", "intent_l2", "scenario", "contact", "reveal", "final"];

// Detect embed mode (mirrors Widget.jsx) so we can adjust drawer positioning
// when running inside an auto-resized iframe.
function isEmbedded() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("embed") === "core";
}

const RISK_CFG = {
  likely_up: { icon: TrendingUp, label: "Likely Up", bg: "bg-signal-up-bg", text: "text-signal-up" },
  volatile: { icon: Activity, label: "Volatile", bg: "bg-signal-volatile-bg", text: "text-signal-volatile" },
  stable: { icon: ShieldCheck, label: "Stable", bg: "bg-signal-stable-bg", text: "text-signal-stable" },
};

// Canadian minimum down payment rules (CMHC + insurable cap raised to $1.5M, Dec 2024)
//  • Owner-occupied or second home, ≤ $500,000 ............ 5%
//  • Owner-occupied or second home, $500,001 – $1,499,999 . 5% of first $500k + 10% of remainder
//  • Owner-occupied or second home, ≥ $1,500,000 .......... 20% (no insured mortgage available)
//  • Rental / investment property ......................... 20% (regardless of price)
function calcMinDownPayment(price, propertyUse) {
  const p = Number(price) || 0;
  if (p <= 0) return { amount: 0, pct: 0, rule: "" };
  if (propertyUse === "rental") {
    return { amount: p * 0.2, pct: 20, rule: "Rental — 20% required" };
  }
  if (p >= 1_500_000) {
    return { amount: p * 0.2, pct: 20, rule: "$1.5M+ — 20% required (uninsurable)" };
  }
  if (p <= 500_000) {
    return { amount: p * 0.05, pct: 5, rule: "5% on first $500k" };
  }
  const amt = 25_000 + (p - 500_000) * 0.10;
  return { amount: amt, pct: Math.round((amt / p) * 1000) / 10, rule: "5% on first $500k + 10% on remainder" };
}

// ---------- Drawer shell ("sheet" = the approved right-side redesign; `?drawer=classic` shows the old modal) ----
const VARIANTS = {
  sheet: {
    keepCopy: true,
    headings: { intent: "What's your situation?" },
    subs: { intent: "We'll tailor the rates and strategy to fit." },
  },
};
const INTENTS = [
  { value: "buying_signed", l1: "buying", icon: Zap, title: "I've signed an offer to purchase" },
  { value: "buying_planning", l1: "buying", icon: Compass, title: "I'm planning to buy" },
  { value: "renewal", l1: "renew_refi", icon: RefreshCw, title: "My mortgage is up for renewal" },
  { value: "refinance", l1: "renew_refi", icon: FileText, title: "I want to refinance" },
];
const STEP_LABELS = { inquire: "Inquire about this rate", intent_l1: "Step 1 of 3", intent_l2: "Step 1 of 3", scenario: "Step 2 of 3", contact: "Step 3 of 3", reveal: "Your Rates", final: "You're Set" };
const STEP_PROGRESS = { inquire: 0.5, intent_l1: 0.2, intent_l2: 0.2, scenario: 0.45, contact: 0.7, reveal: 0.9, final: 1 };

function useDrawerVariant(prop) {
  const { search } = useLocation();
  const q = new URLSearchParams(search).get("drawer");
  if (prop === "classic" || q === "classic") return "classic";
  return "sheet";
}

function IntentPicker({ v, onSelect }) {
  return (
    <div className="hm-fv-step" data-testid="intent-picker">
      <h2 className="hm-fv-h2 hm-fv-h2--nodot">{v.headings.intent}</h2>
      <p className="hm-fv-sub">{v.subs.intent}</p>
      <div className="hm-fv-chips">
        {INTENTS.map((o, i) => (
          <button key={o.value} type="button" onClick={() => onSelect(o)} style={{ animationDelay: `${i * 60}ms` }} className="hm-fv-chip" data-testid={`intent-chip-${o.value.replace(/_/g, "-")}`}>
            <o.icon size={18} strokeWidth={2.25} /><span>{o.title}</span><ArrowRight size={16} strokeWidth={2.5} className="hm-fv-chip-arrow" />
          </button>
        ))}
      </div>
      <p className="hm-fv-fine">Free · No commitment · No credit check</p>
    </div>
  );
}

export default function FunnelDrawer({
  open,
  onClose,
  config = {},
  variant: variantProp,
  // Back-compat: several callers pass these at the top level rather than
  // inside `config`. Prefer top-level when present, fall back to config so
  // existing Widget.jsx wiring keeps working unchanged.
  mode: modeProp,
  presetLevel1: presetLevel1Prop,
  presetIntent: presetIntentProp,
  inquireContext: inquireContextProp,
}) {
  const mode = modeProp ?? config.mode ?? "full";
  const presetLevel1 = presetLevel1Prop ?? config.presetLevel1 ?? null;
  // `presetIntent` jumps the funnel STRAIGHT to the scenario step with
  // intent already set — used by the homepage 'Already in a variable
  // rate?' CTA so the visitor lands directly on the Switch-to-Fixed
  // scenario form instead of being forced through L1 + L2 selection.
  const presetIntent = presetIntentProp ?? config.presetIntent ?? null;
  const inquireContext = inquireContextProp ?? config.inquireContext ?? null;
  const entryCta = config.entryCta ?? config.intent ?? null;
  const navigate = useNavigate();
  const urlVariant = useDrawerVariant(variantProp ?? config.variant);

  const computeInitialStep = () => {
    if (mode === "inquire") return "inquire";
    if (presetIntent) return "scenario";
    if (presetLevel1) return "intent_l2";
    return "intent_l1";
  };
  const computeInitialL1 = () => {
    if (presetLevel1) return presetLevel1;
    // Map a preset intent back to its L1 bucket so the back arrow
    // returns to the correct screen if the visitor wants to change it.
    if (presetIntent === "switch_to_fixed" || presetIntent === "renewal" || presetIntent === "refinance") {
      return "renew_refi";
    }
    if (presetIntent === "buying_signed" || presetIntent === "buying_planning") {
      return "buying";
    }
    return null;
  };

  const initialStep = computeInitialStep();

  const [step, setStep] = useState(initialStep);
  const [intentL1, setIntentL1] = useState(computeInitialL1()); // 'buying' | 'renew_refi'
  const [intent, setIntent] = useState(presetIntent || null);
  const [scenario, setScenario] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", phone: "", wantsCall: false });
  const [leadId, setLeadId] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loanAmount, setLoanAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLender, setSelectedLender] = useState(null);
  const [selectedLenderId, setSelectedLenderId] = useState(null);

  // When embedded, keep track of the parent's viewport rect (broadcast by
  // embed.js) so the drawer can render inside the user's actual visible
  // window even if the iframe is taller than the viewport.
  const embedded = isEmbedded();
  const [parentViewport, setParentViewport] = useState(null);
  // Partner embeds (iframe) keep the centered modal — a right sheet can't anchor to the host page's viewport.
  const variant = embedded ? "classic" : urlVariant;
  const v = VARIANTS.sheet;

  useEffect(() => {
    if (!embedded || typeof window === "undefined") return undefined;
    const onMsg = (e) => {
      const d = e.data || {};
      if (d.type !== "hello-mortgage-parent-viewport") return;
      setParentViewport({
        iframeTop: d.iframe_top_in_parent || 0,
        viewportHeight: d.parent_viewport_height || 0,
      });
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [embedded]);

  // Ask the parent to scroll the iframe into view as soon as the drawer opens
  // so the drawer is rendered inside the visible window.
  useEffect(() => {
    if (!embedded || !open || typeof window === "undefined") return;
    try {
      window.parent.postMessage({ type: "hello-mortgage-drawer-open" }, "*");
    } catch (e) {
      /* no-op */
    }
  }, [embedded, open]);

  // Sync to drawer config when reopening with different mode
  useEffect(() => {
    if (open) {
      setStep(computeInitialStep());
      setIntentL1(computeInitialL1());
      if (presetIntent) setIntent(presetIntent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, presetLevel1, presetIntent]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(computeInitialStep());
        setIntentL1(computeInitialL1());
        setIntent(presetIntent || null);
        setScenario({});
        setContact({ name: "", email: "", phone: "", wantsCall: false });
        setLeadId(null);
        setMatches([]);
        setLoanAmount(0);
        setError(null);
        setSelectedLender(null);
        setSelectedLenderId(null);
      }, 320);
      return () => clearTimeout(t);
    }
  }, [open, mode, presetLevel1]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      const orig = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = orig;
      };
    }
  }, [open]);

  const goBack = () => {
    if (mode === "inquire") return onClose();
    const idx = STEPS.indexOf(step);
    if (idx <= 0) return onClose();
    // If preset level 1 was provided, skip back to intent_l1 means just close
    if (presetLevel1 && step === "intent_l2") return onClose();
    setStep(STEPS[idx - 1]);
    setError(null);
  };

  // ----- Inquire mode (single-step, RateHub-style)
  const submitInquire = async (form) => {
    setError(null);
    if (!form.name?.trim() || !form.email?.trim() || !form.phone?.trim()) {
      setError("Name, email, and phone are required.");
      return;
    }
    const ec = checkEmail(form.email);
    if (!ec.valid) { setError(ec.error); return; }
    if (!phoneLooksValid(form.phone)) { setError("That phone number looks short — please include the area code."); return; }
    setSubmitting(true);
    try {
      // Rate-inquiry flow: route to the dedicated rate_inquiry drip
      // campaign instead of the broader buying/renewal/refinance
      // sequences. The lead's mortgage_type drives the RI-A/RI-B/RI-C
      // sub-track inside the rate_inquiry_v1 campaign. The rate card
      // context (rate, term, stability, lender_category) is passed
      // through so the drip emails can reference the SPECIFIC rate
      // the visitor clicked on rather than generic featured rates.
      const scenarioPayload = {
        city: form.location || null,
        purchase_price: form.amount && form.mortgage_type === "buying"
          ? parseFloat(form.amount)
          : null,
        property_value: form.amount && form.mortgage_type !== "buying"
          ? parseFloat(form.amount)
          : null,
        mortgage_amount: form.amount ? parseFloat(form.amount) : null,
        mortgage_type: form.mortgage_type || "buying",
        rate: inquireContext?.rate ?? null,
        term: inquireContext?.term || null,
        stability: inquireContext?.stability || null,
        lender_category: inquireContext?.lender_category
          || inquireContext?.tier_label
          || null,
        lender_name: inquireContext?.lender_name || null,
        rate_id: inquireContext?.rate_id || null,
        term_pref: inquireContext?.term || null,
      };
      if (config.city && !scenarioPayload.city) scenarioPayload.city = config.city;
      const metaEventId = newMetaEventId();
      const resp = await publicApi.post("/lead", {
        meta_event_id: metaEventId,
        intent: "rate_inquiry",
        scenario: scenarioPayload,
        name: form.name,
        email: form.email,
        phone: form.phone,
        consent_marketing: !!form.consent,
        source_partner_slug: getPartnerSlug() || null,
        entry_cta: entryCta,
        attribution: attributionPayload(), url: window.location.href, is_test: isTestSession(),
      });
      rememberVisitor({ email: form.email, phone: form.phone, firstName: form.name?.split(" ")[0], lastName: form.name?.split(" ").slice(1).join(" ") });
      pixelTrack("Lead", { content_name: "Site funnel" }, metaEventId);
      setLeadId(resp.data.lead_id);
      setIntent("rate_inquiry");
      setStep("final");
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitContactGate = async () => {
    setError(null);
    if (intent === "switch_to_fixed" && (!contact.first_name?.trim() || !contact.last_name?.trim())) {
      setError(!contact.first_name?.trim() ? "Please add your first name." : "Please add your last name.");
      return;
    }
    if (!contact.name?.trim() || !contact.email?.trim() || !contact.phone?.trim()) {
      setError("Name, email, and phone are required.");
      return;
    }
    const ec = checkEmail(contact.email);
    if (!ec.valid) { setError(ec.error); return; }
    if (!phoneLooksValid(contact.phone)) {
      setError("That phone number looks short — please include the area code.");
      return;
    }
    setSubmitting(true);
    try {
      // Mirror the renewal "original down payment" segment into the
      // boolean field the matcher / CRM consumes — keeps the readable
      // string AND a machine-friendly flag.
      const scenarioForLead = { ...scenario };
      if (scenario.original_down_pct === "lt20") {
        scenarioForLead.original_down_under_20 = true;
      } else if (scenario.original_down_pct === "gte20") {
        scenarioForLead.original_down_under_20 = false;
      }
      // Coerce numeric Switch-to-Fixed scenario fields so the backend
      // Pydantic validation accepts them (string → number).
      if (intent === "switch_to_fixed") {
        const num = (k) => {
          const v = parseFloat(scenarioForLead[k]);
          if (!Number.isNaN(v)) scenarioForLead[k] = v;
        };
        num("current_balance");
        num("current_variable_rate_pct");
        num("current_monthly_payment");
        if (!scenarioForLead.current_monthly_payment) delete scenarioForLead.current_monthly_payment;
        const t = parseInt(scenarioForLead.lender_offered_fixed_term_years, 10);
        scenarioForLead.lender_offered_fixed_term_years = Number.isNaN(t) ? 5 : t;  // "Not sure" assumes 5 years
        if (!scenarioForLead.lender_offered_fixed_rate_pct) delete scenarioForLead.lender_offered_fixed_rate_pct;
        delete scenarioForLead.property_value;
        if (contact.first_name || contact.last_name) { scenarioForLead.first_name = contact.first_name; scenarioForLead.last_name = contact.last_name; }
        num("amort_remaining_years");
        num("property_value");
        num("lender_offered_fixed_rate_pct");
      }
      if (config.city && !scenarioForLead.city) scenarioForLead.city = config.city;
      const metaEventId = newMetaEventId();
      const leadResp = await publicApi.post("/lead", {
        meta_event_id: metaEventId,
        intent,
        scenario: scenarioForLead,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        wants_callback: !!contact.wantsCall,
        consent_marketing: false,
        source_partner_slug: getPartnerSlug() || null,
        entry_cta: entryCta,
        attribution: attributionPayload(), url: window.location.href, is_test: isTestSession(),
      });
      rememberVisitor({ email: contact.email, phone: contact.phone, firstName: contact.name?.split(" ")[0], lastName: contact.name?.split(" ").slice(1).join(" ") });
      pixelTrack("Lead", { content_name: "Site funnel" }, metaEventId);
      const newLeadId = leadResp.data.lead_id;
      setLeadId(newLeadId);

      // Switch-to-Fixed flow has its own results page (3-way comparison +
      // BoC scenario toggles + education panel). Everything else flows
      // into the standard /compare lender results page.
      if (intent === "switch_to_fixed") {
        gaEvent("SwitchFormSubmit", { content_name: "switch_to_fixed" });
        const switchParams = new URLSearchParams({ lead: newLeadId });
        const currentEmbed = new URLSearchParams(window.location.search).get("embed");
        if (currentEmbed) switchParams.set("embed", currentEmbed);
        onClose?.();
        navigate(`/switch-analysis?${switchParams.toString()}`);
        return;
      }

      // Navigate the iframe to the full Compare page. Funnel modal closes
      // once the route changes; the new page picks up scenario from the
      // lead doc via /api/public/lead/{lead_id}.
      const queryArgs = {
        lead: newLeadId,
        intent: intent || "",
        type: "fixed",
        term: "5yr",
        amort: "25",
        freq: "monthly",
        use: scenario.property_use || "owner",
      };
      // Preserve `embed=` so the Compare route also broadcasts its height
      // back to the parent frame — otherwise clicking "View more lenders"
      // expands content past the iframe's fixed height and clips the list.
      const currentEmbed = new URLSearchParams(window.location.search).get("embed");
      if (currentEmbed) queryArgs.embed = currentEmbed;
      const params = new URLSearchParams(queryArgs);

      // Persist so an iframe parent-page refresh auto-resumes Compare
      // instead of dumping the visitor back onto the home widget.
      saveFunnelSession({ leadId: newLeadId, query: queryArgs });

      onClose?.();
      navigate(`/compare?${params.toString()}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const sendAction = async (action, lenderName, lenderId, note) => {
    if (!leadId) return;
    try {
      await publicApi.post("/lead-action", {
        lead_id: leadId,
        action,
        selected_lender: lenderName,
        selected_lender_id: lenderId,
        note,
      });
    } catch (e) {
      // Best-effort only
    }
  };

  if (!open) return null;

  // Compute embed-aware container positioning. Inside an iframe with
  // scrolling=no whose height matches its content, `position: fixed` is
  // anchored to the iframe's content (full height) rather than the parent's
  // viewport — which can drop the drawer hundreds of pixels below the user's
  // visible window. So in embed mode we switch to absolute positioning that
  // tracks the parent's viewport rect.
  const shellByVariant = {
    classic: { outer: "items-end md:items-center justify-center", panel: "w-full md:max-w-2xl bg-white rounded-t-3xl md:rounded-3xl max-h-[92vh] md:max-h-[88vh] animate-slide-up md:animate-fade-in" },
    sheet: { outer: "items-end md:items-stretch justify-end", panel: "w-full md:w-[520px] md:max-w-[92vw] rounded-t-3xl md:rounded-none max-h-[92vh] md:max-h-none md:h-full animate-slide-up md:animate-slide-in-right" },
  }[variant];
  let outerStyle = {};
  let outerClass = `fixed inset-0 z-[100] flex ${shellByVariant.outer} animate-fade-in`;
  if (embedded && parentViewport && parentViewport.viewportHeight > 0) {
    // Where does the parent viewport sit within the iframe's coordinate system?
    // iframeTop = iframe.getBoundingClientRect().top in parent (negative when
    // iframe top is scrolled above parent viewport top).
    const topWithinIframe = Math.max(0, -parentViewport.iframeTop);
    outerClass =
      "absolute left-0 right-0 z-[100] flex items-start md:items-center justify-center animate-fade-in";
    outerStyle = {
      top: `${topWithinIframe}px`,
      height: `${parentViewport.viewportHeight}px`,
    };
  }
  const showBack = variant === "classic" ? step !== "intent_l1" && step !== "final" : !["intent_l1", "intent_l2", "final", "inquire"].includes(step);

  return (
    <div className={outerClass} style={outerStyle} data-testid="funnel-drawer" data-variant={variant}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-brand-navy/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={`hm-funnel hm-funnel--${variant} relative shadow-2xl overflow-hidden flex flex-col ${shellByVariant.panel}`}
        style={
          embedded && parentViewport && parentViewport.viewportHeight > 0
            ? { maxHeight: `${Math.max(280, parentViewport.viewportHeight - 32)}px`, marginTop: "16px", marginBottom: "16px" }
            : undefined
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="funnel-title"
      >
        {/* Header */}
        {variant === "classic" ? (
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          {showBack ? (
            <button
              type="button"
              onClick={goBack}
              data-testid="funnel-back"
              className="text-slate-400 hover:text-brand-navy transition-colors -ml-1.5 p-1.5"
              aria-label="Back"
            >
              <ArrowLeft size={20} strokeWidth={2.5} />
            </button>
          ) : (
            <div className="w-9" />
          )}
          <StepIndicator step={step} intent={intent} />
          <button
            type="button"
            onClick={onClose}
            data-testid="funnel-close"
            className="text-slate-400 hover:text-brand-navy transition-colors -mr-1.5 p-1.5"
            aria-label="Close"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>
        ) : (
        <div className="hm-fv-head shrink-0" data-testid="funnel-variant-head">
          {<div className="hm-fv-progress" aria-hidden="true"><span style={{ width: `${STEP_PROGRESS[step] * 100}%` }} /></div>}
          <div className="hm-fv-headrow">
            <div className="hm-fv-headleft">
              {showBack && <button type="button" onClick={goBack} data-testid="funnel-back" className="hm-fv-icon" aria-label="Back"><ArrowLeft size={18} strokeWidth={2.5} /></button>}
              <span id="funnel-title" className="hm-fv-eyebrow">{intent === "switch_to_fixed" && step === "scenario" ? "Step 1 of 2" : intent === "switch_to_fixed" && step === "contact" ? "Step 2 of 2" : STEP_LABELS[step]}</span>
            </div>
            <button type="button" onClick={onClose} data-testid="funnel-close" className="hm-fv-icon" aria-label="Close"><X size={18} strokeWidth={2.5} /></button>
          </div>
        </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {step === "inquire" && (
            <InquireForm
              context={inquireContext}
              error={error}
              submitting={submitting}
              onSubmit={submitInquire}
            />
          )}
          {variant !== "classic" && (step === "intent_l1" || step === "intent_l2") && (
            <IntentPicker v={v} onSelect={(o) => { setIntentL1(o.l1); setIntent(o.value); setStep("scenario"); }} />
          )}
          {variant === "classic" && step === "intent_l1" && (
            <IntentLevel1
              onSelect={(v) => {
                setIntentL1(v);
                setStep("intent_l2");
              }}
            />
          )}
          {variant === "classic" && step === "intent_l2" && (
            <IntentLevel2
              level1={intentL1}
              onSelect={(v) => {
                setIntent(v);
                setStep("scenario");
              }}
            />
          )}
          {step === "scenario" && (
            <ScenarioForm
              intent={intent}
              scenario={scenario}
              onChange={setScenario}
              onContinue={() => setStep("contact")}
            />
          )}
          {step === "contact" && (
            <ContactGate
              intent={intent}
              contact={contact}
              onChange={setContact}
              error={error}
              submitting={submitting}
              onSubmit={submitContactGate}
            />
          )}
          {step === "reveal" && (
            <RevealResults
              matches={matches}
              loanAmount={loanAmount}
              onInquire={(match) => {
                setSelectedLender(match.lender_name);
                setSelectedLenderId(match.lender_id);
                sendAction("secure_rate", match.lender_name, match.lender_id);
                setStep("final");
              }}
            />
          )}
          {step === "final" && (
            <FinalActions
              selectedLender={selectedLender}
              contact={contact}
              onAction={async (action) => {
                await sendAction(action, selectedLender, selectedLenderId);
              }}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Step indicator -----------------------------------------------
function StepIndicator({ step, intent }) {
  const labels = intent === "switch_to_fixed" ? { scenario: "Step 1 of 2", contact: "Step 2 of 2", reveal: "Your Rates", final: "You're Set" } : {
    inquire: "Inquire about this rate",
    intent_l1: "Step 1 of 3",
    intent_l2: "Step 1 of 3",
    scenario: "Step 2 of 3",
    contact: "Step 3 of 3",
    reveal: "Your Rates",
    final: "You're Set",
  };
  return (
    <div
      id="funnel-title"
      className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 font-body"
    >
      {labels[step] || ""}
    </div>
  );
}

// ---------- Inquire form (RateHub-style single-step) --------------------
function InquireForm({ context, error, submitting, onSubmit }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    mortgage_type: "buying",
    amount: "",
    location: "Calgary, AB",
    consent: false,
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const risk = RISK_CFG[context?.risk] || RISK_CFG.stable;
  const RiskIcon = risk.icon;
  const privileges = context?.privileges || null;

  return (
    <div className="px-5 sm:px-8 py-7 sm:py-9 animate-fade-in">
      {/* Rate context preview */}
      {context && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mb-6 flex flex-col gap-4" data-testid="inquire-context-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1 font-body">
                {context.label}
              </div>
              <div className="flex items-baseline gap-3">
                <span className="text-3xl sm:text-4xl font-bold tracking-tighter font-display text-brand-navy leading-none">
                  {context.rate != null ? `${Number(context.rate).toFixed(2)}%` : "—"}
                </span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest font-body ${risk.bg} ${risk.text}`}>
                  <RiskIcon size={10} strokeWidth={2.5} />
                  {risk.label}
                </span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5 font-body">
                {context.tier_label || "Featured rate"} · sourced live this morning
              </div>
            </div>
          </div>

          {/* Lender privileges: pre-payment %, rate hold, pre-approval */}
          {privileges && (
            <div
              className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-slate-200"
              data-testid="inquire-privileges"
            >
              <PrivilegeCell
                label="Monthly prepay"
                value={`${privileges.monthly_prepayment_pct}%`}
                testid="inquire-priv-monthly"
              />
              <PrivilegeCell
                label="Lump sum prepay"
                value={`${privileges.lump_sum_prepayment_pct}%`}
                testid="inquire-priv-lumpsum"
              />
              <PrivilegeCell
                label="Rate hold"
                value={`${privileges.rate_hold_days} days`}
                testid="inquire-priv-hold"
              />
              <PrivilegeCell
                label="Pre-approval"
                value={privileges.pre_approval_available ? "Available" : "Not offered"}
                testid="inquire-priv-preapproval"
              />
            </div>
          )}
        </div>
      )}

      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-navy font-display mb-2">
        Get this rate in your inbox.
      </h2>
      <p className="text-sm sm:text-base text-slate-500 font-body mb-7">
        Our team will reach out within 1 business hour with the lender details, your
        actual qualifying rate, and a strategy fit for your scenario.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Full name">
          <input
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Jane Smith"
            data-testid="inquire-name"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400"
          />
        </Field>
        <Field label="Mortgage type">
          <select
            value={form.mortgage_type}
            onChange={(e) => update("mortgage_type", e.target.value)}
            data-testid="inquire-mortgage-type"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white"
          >
            <option value="buying">Buying a home</option>
            <option value="renewing">Renewing</option>
            <option value="refinancing">Refinancing</option>
          </select>
        </Field>
        <Field label="Phone">
          <input
            type="tel"
            inputMode="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="403 555 1234"
            data-testid="inquire-phone"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400"
          />
        </Field>
        <Field label="Approximate mortgage amount" hint="Optional — best estimate is fine.">
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-display">$</span>
            <input
              type="number"
              inputMode="numeric"
              value={form.amount}
              onChange={(e) => update("amount", e.target.value)}
              placeholder="500,000"
              data-testid="inquire-amount"
              className="w-full pl-8 pr-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-display tabular-nums bg-white text-brand-navy placeholder:text-slate-400"
            />
          </div>
        </Field>
        <Field label="Email">
          <input
            type="email"
            inputMode="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="jane@example.com"
            data-testid="inquire-email"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400"
          />
          {form.email && checkEmail(form.email).suggestion && (
            <button type="button" className="mt-1 text-xs text-[#ff3356] underline font-body" data-testid="inquire-email-suggestion" onClick={() => update("email", checkEmail(form.email).suggestion)}>
              {checkEmail(form.email).error} Click to fix.
            </button>
          )}
        </Field>
        <Field label="Property location">
          <input
            type="text"
            value={form.location}
            onChange={(e) => update("location", e.target.value)}
            placeholder="Calgary, AB"
            data-testid="inquire-location"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400"
          />
        </Field>
      </div>

      <label className="flex items-start gap-3 mt-6 cursor-pointer" data-testid="inquire-consent-label">
        <input
          type="checkbox"
          checked={form.consent}
          onChange={(e) => update("consent", e.target.checked)}
          data-testid="inquire-consent"
          className="mt-0.5 w-4 h-4 accent-brand-red shrink-0"
        />
        <span className="text-xs text-slate-500 font-body leading-relaxed">
          Join our VIP Club for exclusive mortgage news, rate updates, and monthly giveaways — no spam, just the good stuff. By checking this box, you agree to our{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-navy underline hover:text-brand-red"
            data-testid="inquire-consent-terms-link"
            onClick={(e) => e.stopPropagation()}
          >
            Terms of Service
          </a>
          {" "}and{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-navy underline hover:text-brand-red"
            data-testid="inquire-consent-privacy-link"
            onClick={(e) => e.stopPropagation()}
          >
            Privacy Policy
          </a>
          . Unsubscribe anytime.
        </span>
      </label>

      {error && (
        <div className="text-sm text-signal-up font-body mt-4" data-testid="inquire-error">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={() => onSubmit(form)}
        disabled={submitting}
        data-testid="inquire-submit"
        className="w-full mt-6 bg-brand-red text-white rounded-xl py-4 font-bold text-base font-body inline-flex items-center justify-center gap-2 hover:bg-brand-red-hover transition-colors disabled:opacity-70"
      >
        {submitting ? "Sending…" : "Inquire about this rate"}
        {!submitting && <ArrowRight size={18} strokeWidth={2.5} />}
      </button>

      <div className="flex items-center justify-center gap-2 text-[11px] text-slate-400 font-body mt-4">
        <Lock size={12} />
        <span>Encrypted submission. We will never sell your details.</span>
      </div>
    </div>
  );
}

// ---------- Intent level 1 -----------------------------------------------
function PrivilegeCell({ label, value, testid }) {
  return (
    <div data-testid={testid} className="text-center sm:text-left">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 font-body">
        {label}
      </div>
      <div className="text-sm font-bold text-brand-navy font-display mt-0.5 leading-tight">
        {value}
      </div>
    </div>
  );
}

function IntentLevel1({ onSelect }) {
  return (
    <div className="px-5 sm:px-8 py-7 sm:py-10 animate-fade-in">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-navy font-display mb-2">
        What's your situation?
      </h2>
      <p className="text-sm sm:text-base text-slate-500 font-body mb-7 sm:mb-8">
        We'll tailor the rates and strategy to fit.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <BigChoice
          icon={Home}
          title="Buying a home"
          subtitle="Purchase or pre-approval"
          onClick={() => onSelect("buying")}
          testid="intent-l1-buying"
        />
        <BigChoice
          icon={RefreshCw}
          title="Renewing or refinancing"
          subtitle="Existing mortgage"
          onClick={() => onSelect("renew_refi")}
          testid="intent-l1-renew-refi"
        />
      </div>
    </div>
  );
}

// ---------- Intent level 2 -----------------------------------------------
function IntentLevel2({ level1, onSelect }) {
  const buying = [
    {
      icon: Zap,
      title: "I've signed an offer to purchase",
      value: "buying_signed",
    },
    {
      icon: Compass,
      title: "I'm planning to buy",
      value: "buying_planning",
    },
  ];
  const renewRefi = [
    {
      icon: RefreshCw,
      title: "My mortgage is up for renewal",
      value: "renewal",
    },
    {
      icon: FileText,
      title: "I want to refinance",
      value: "refinance",
    },
  ];
  const list = level1 === "buying" ? buying : renewRefi;
  return (
    <div className="px-5 sm:px-8 py-7 sm:py-10 animate-fade-in">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-navy font-display mb-2">
        Which one fits?
      </h2>
      <p className="text-sm sm:text-base text-slate-500 font-body mb-7 sm:mb-8">
        Pick the option closest to your timeline.
      </p>
      <div className="space-y-3">
        {list.map((opt) => (
          <BigChoice
            key={opt.value}
            icon={opt.icon}
            title={opt.title}
            onClick={() => onSelect(opt.value)}
            testid={`intent-l2-${opt.value.replace(/_/g, "-")}`}
            full
          />
        ))}
      </div>
    </div>
  );
}

function BigChoice({ icon: Icon, title, subtitle, onClick, testid, full }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`group relative bg-white border-2 border-slate-200 hover:border-brand-navy hover:shadow-lg rounded-2xl p-5 text-left transition-all ${full ? "w-full" : ""}`}
    >
      <div className="flex items-start gap-4">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-brand-navy text-white shrink-0 transition-transform group-hover:scale-105">
          <Icon size={20} strokeWidth={2.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-base sm:text-lg font-bold text-brand-navy leading-snug font-display">
            {title}
          </div>
          {subtitle && (
            <div className="text-xs sm:text-sm text-slate-500 mt-0.5 font-body">{subtitle}</div>
          )}
        </div>
        <ArrowRight
          size={18}
          className="text-slate-300 group-hover:text-brand-red group-hover:translate-x-0.5 transition-all shrink-0 mt-1"
          strokeWidth={2.5}
        />
      </div>
    </button>
  );
}

// ---------- Scenario form ------------------------------------------------
function ScenarioForm({ intent, scenario, onChange, onContinue }) {
  const isBuying = intent?.startsWith("buying");
  const isSignedBuyer = intent === "buying_signed";
  const isRefi = intent === "refinance";
  const isRenewal = intent === "renewal";
  const isSwitch = intent === "switch_to_fixed";

  const update = (k, v) => onChange({ ...scenario, [k]: v });
  // Pre-selected options must save without a tap: write the defaults into state on open.
  useEffect(() => {
    if (!isSwitch) return;
    const defaults = {};
    if (!scenario.lender_offered_fixed_term_years) defaults.lender_offered_fixed_term_years = "not_sure";
    if (!scenario.property_use) defaults.property_use = "owner";
    if (Object.keys(defaults).length) onChange({ ...scenario, ...defaults });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSwitch]);

  // Track whether the user has manually edited the down payment so we don't
  // clobber their input when purchase price changes.
  const [downEdited, setDownEdited] = useState(false);

  // Live computed math
  const purchasePrice = parseFloat(scenario.purchase_price) || 0;
  const propertyUse = scenario.property_use || "owner";
  const minDown = calcMinDownPayment(purchasePrice, propertyUse);
  const downPayment = parseFloat(scenario.down_payment) || 0;
  const downPct = purchasePrice ? Math.round((downPayment / purchasePrice) * 1000) / 10 : 0;
  const loanForPurchase = Math.max(0, purchasePrice - downPayment);
  const belowMin = isBuying && purchasePrice > 0 && downPayment > 0 && downPayment < minDown.amount;

  // Auto-prefill down payment to Canadian minimum whenever purchase price or
  // property use changes — but only if the user hasn't manually typed a value.
  useEffect(() => {
    if (!isBuying) return;
    if (downEdited) return;
    if (purchasePrice <= 0) return;
    const rounded = Math.round(minDown.amount);
    if (rounded > 0 && String(rounded) !== String(scenario.down_payment || "")) {
      onChange({ ...scenario, down_payment: rounded });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchasePrice, propertyUse, isBuying, downEdited]);

  const propValue = parseFloat(scenario.property_value) || 0;
  const balance = parseFloat(scenario.mortgage_balance) || 0;
  const additional = parseFloat(scenario.additional_funds) || 0;
  const maxLtv = isRefi ? 0.8 : 0.8;
  const maxBorrow = Math.max(0, propValue * maxLtv - balance);
  const totalLoanRefi = balance + Math.min(additional, maxBorrow);

  // Item 13: name only the FIRST empty required field.
  const switchRequired = [
    ["current_balance", "Mortgage balance", (v) => (parseFloat(v) || 0) >= 50000],
    ["current_variable_rate_pct", "Current variable rate", (v) => (parseFloat(v) || 0) >= 1],
    ["amort_remaining_years", "Years left to pay off your mortgage", (v) => (parseFloat(v) || 0) >= 1],
    ["original_down_pct", "Down payment answer", (v) => !!v],
    ["home_value_range", "Rough home value", (v) => !!v],
    ["current_lender", "Current lender", (v) => !!(v || "").trim()],
    ["maturity_date", "Maturity date", (v) => /^\d{4}-\d{2}$/.test(v || "")],
    ["asked_lender", "Answer about asking your lender", (v) => !!v],
  ];
  const switchMissing = isSwitch ? (switchRequired.find(([k, , ok]) => !ok(scenario[k])) || null) : null;
  const missingHint = (k) => (switchMissing && switchMissing[0] === k ? <p className="mt-1 text-xs text-brand-red font-body" data-testid={`missing-${k}`}>Please add your {switchMissing[1].toLowerCase()} to continue.</p> : null);

  const canContinue = isBuying
    ? purchasePrice >= 50000 && downPayment >= 0 && (isSignedBuyer ? !!scenario.closing_date : true)
    : isRenewal
    ? propValue >= 50000 && balance >= 0 && !!scenario.original_down_pct && !!scenario.renewal_date
    : isSwitch
    ? !switchMissing
    : propValue >= 50000 && balance >= 0;

  const scenarioHeading = isSignedBuyer
    ? "A few quick details about your new home."
    : isBuying
    ? "A few quick details about the home you're considering."
    : isSwitch
    ? "Tell us about your current variable mortgage."
    : "A few quick details about your current mortgage.";

  return (
    <div className="px-5 sm:px-8 py-7 sm:py-9 animate-fade-in">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-navy font-display mb-7">
        {scenarioHeading}
      </h2>

      <div className="space-y-5">
        {!isSwitch && (<>
        <Field label="City" hint="Where's the property located?">
          <input
            type="text"
            value={scenario.city || ""}
            onChange={(e) => update("city", e.target.value)}
            placeholder="Calgary, AB"
            data-testid="scenario-city"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400"
          />
        </Field>

        <Field label="Property use">
          <SegmentedButtons
            value={scenario.property_use || "owner"}
            onChange={(v) => update("property_use", v)}
            options={[
              { value: "owner", label: "Owner-occupied" },
              { value: "second-home", label: "Second home" },
              { value: "rental", label: "Rental" },
            ]}
            testid="scenario-property-use"
          />
        </Field>
        </>)}

        {isBuying ? (
          <>
            <Field label="Purchase price" hint="Best estimate is fine">
              <MoneyInput
                value={scenario.purchase_price}
                onChange={(v) => update("purchase_price", v)}
                placeholder="500,000"
                testid="scenario-purchase-price"
              />
            </Field>
            <Field
              label="Down payment"
              hint={
                purchasePrice > 0 ? (
                  <>
                    {downPct > 0 ? <>{downPct}% of purchase price · </> : null}
                    Min required:{" "}
                    <span className="font-bold text-brand-navy" data-testid="scenario-min-down-amount">
                      {fmtMoney(Math.round(minDown.amount))}
                    </span>{" "}
                    ({minDown.rule})
                  </>
                ) : (
                  "How much you're putting down — we'll prefill the Canadian minimum once you enter a price."
                )
              }
            >
              <MoneyInput
                value={scenario.down_payment}
                onChange={(v) => {
                  setDownEdited(true);
                  update("down_payment", v);
                }}
                placeholder={purchasePrice > 0 ? String(Math.round(minDown.amount)) : "100,000"}
                testid="scenario-down-payment"
              />
              {belowMin && (
                <div className="mt-2 text-[11px] text-signal-up font-body" data-testid="scenario-down-below-min">
                  Below Canadian minimum — typical lenders will require at least {fmtMoney(Math.round(minDown.amount))}.
                </div>
              )}
            </Field>

            {isSignedBuyer && (
              <Field
                label="Estimated closing date"
                hint="When does possession transfer? Helps us align rate-hold timing."
              >
                <input
                  type="date"
                  value={scenario.closing_date || ""}
                  onChange={(e) => update("closing_date", e.target.value)}
                  data-testid="scenario-closing-date"
                  min={new Date().toISOString().slice(0, 10)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy"
                />
              </Field>
            )}
          </>
        ) : isSwitch ? (
          <>
            <Field label="Mortgage balance" hint="What's left on the loan today?">
              <MoneyInput value={scenario.current_balance} onChange={(v) => update("current_balance", v)} placeholder="450,000" testid="scenario-current-balance" />
              {missingHint("current_balance")}
            </Field>

            <Field label="Your current variable rate" hint="The full rate you're paying today (e.g. Prime − 0.50% = 4.95%).">
              <PercentInput value={scenario.current_variable_rate_pct} onChange={(v) => update("current_variable_rate_pct", v)} placeholder="4.95" testid="scenario-current-variable-rate" />
              {missingHint("current_variable_rate_pct")}
            </Field>

            <Field label="Years left to pay off your mortgage" hint="Your remaining amortization — how long until the whole mortgage is paid off, not the years left in your current term.">
              <input type="number" inputMode="decimal" step="0.5" value={scenario.amort_remaining_years || ""} onChange={(e) => update("amort_remaining_years", e.target.value)} placeholder="23.5" data-testid="scenario-amort-remaining"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-display tabular-nums bg-white text-brand-navy placeholder:text-slate-400" />
              {missingHint("amort_remaining_years")}
            </Field>

            <Field label="Did you put less than 20% down originally?" hint="If yes, your insurance follows you on a switch — you get insured pricing.">
              <SegmentedButtons value={scenario.original_down_pct || ""} onChange={(v) => update("original_down_pct", v)} options={[{ value: "lt20", label: "Less than 20%" }, { value: "gte20", label: "20% or more" }]} testid="scenario-original-down-pct" />
              {missingHint("original_down_pct")}
            </Field>

            <Field label="Rough home value" hint="A range is all we need.">
              <select value={scenario.home_value_range || ""} onChange={(e) => update("home_value_range", e.target.value)} data-testid="scenario-home-value-range" className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400">
                <option value="">Choose a range</option>
                <option value="under_300k">Under $300,000</option>
                <option value="300_500k">$300,000 – $500,000</option>
                <option value="500_750k">$500,000 – $750,000</option>
                <option value="750k_1m">$750,000 – $1,000,000</option>
                <option value="1m_1_5m">$1,000,000 – $1,500,000</option>
                <option value="over_1_5m">Over $1,500,000</option>
              </select>
              {missingHint("home_value_range")}
            </Field>

            <Field label="Current lender">
              <input type="text" value={scenario.current_lender || ""} onChange={(e) => update("current_lender", e.target.value)} placeholder="e.g. TD, Scotiabank, MCAP" data-testid="scenario-current-lender" className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400" />
              {missingHint("current_lender")}
            </Field>

            <Field label="Maturity date" hint="The month your current term ends.">
              <input type="month" value={scenario.maturity_date || ""} onChange={(e) => update("maturity_date", e.target.value)} data-testid="scenario-maturity-date" className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400" />
              {missingHint("maturity_date")}
            </Field>

            <Field label="Have you asked your lender about switching to a fixed rate?">
              <SegmentedButtons value={scenario.asked_lender || ""} onChange={(v) => update("asked_lender", v)} options={[{ value: "yes", label: "Yes" }, { value: "no", label: "Not yet" }]} testid="scenario-asked-lender" />
              {missingHint("asked_lender")}
            </Field>

            <Field label="Your current monthly payment" hint="Optional — leave it blank and we'll work it out from your balance, rate and years left.">
              <MoneyInput value={scenario.current_monthly_payment} onChange={(v) => update("current_monthly_payment", v)} placeholder="2,450" testid="scenario-current-payment" />
            </Field>

            <div className="border-t border-slate-200 pt-5">
              <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-brand-navy mb-2 font-body">
                Optional — has your lender offered you a fixed rate?
              </p>
              <p className="text-[11px] text-slate-500 mb-4 font-body">
                Drop it in here and we'll put their offer next to what we can find. Skip it if you haven't asked yet.
              </p>
              <Field label="Your lender's offer rate">
                <PercentInput value={scenario.lender_offered_fixed_rate_pct} onChange={(v) => update("lender_offered_fixed_rate_pct", v)} placeholder="4.59" testid="scenario-lender-offered-rate" />
              </Field>
              <div className="mt-3">
                <Field label="What term is that offer for?">
                  <select value={String(scenario.lender_offered_fixed_term_years || "not_sure")} onChange={(e) => update("lender_offered_fixed_term_years", e.target.value)} data-testid="scenario-lender-offered-term" className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400">
                    <option value="not_sure">Not sure</option>
                    <option value="1">1 year</option>
                    <option value="2">2 years</option>
                    <option value="3">3 years</option>
                    <option value="4">4 years</option>
                    <option value="5">5 years</option>
                  </select>
                </Field>
              </div>
            </div>
          </>
        ) : (
          <>
            <Field label="Current property value">
              <MoneyInput
                value={scenario.property_value}
                onChange={(v) => update("property_value", v)}
                placeholder="500,000"
                testid="scenario-property-value"
              />
            </Field>
            <Field label="Current mortgage balance">
              <MoneyInput
                value={scenario.mortgage_balance}
                onChange={(v) => update("mortgage_balance", v)}
                placeholder="350,000"
                testid="scenario-mortgage-balance"
              />
            </Field>

            {isRenewal && (
              <Field
                label="Renewal date"
                hint="When does your current term end? We start shopping rates 120 days out — earlier is always better."
              >
                <input
                  type="date"
                  value={scenario.renewal_date || ""}
                  onChange={(e) => update("renewal_date", e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  data-testid="scenario-renewal-date"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-display tabular-nums bg-white text-brand-navy placeholder:text-slate-400"
                />
              </Field>
            )}

            {isRenewal && (
              <Field
                label="Original down payment"
                hint="When you first purchased, did you put less than 20% down (insured) or 20% or more (conventional)? It changes which lenders we route you to."
              >
                <SegmentedButtons
                  value={scenario.original_down_pct || ""}
                  onChange={(v) => update("original_down_pct", v)}
                  options={[
                    { value: "lt20", label: "Less than 20%" },
                    { value: "gte20", label: "20% or more" },
                  ]}
                  testid="scenario-original-down-pct"
                />
              </Field>
            )}

            {isRefi && propValue > 0 && balance > 0 && maxBorrow > 0 && (
              <Field label="Want to access equity?" hint={`Up to ${fmtMoney(maxBorrow)} available based on 80% LTV`}>
                <MoneyInput
                  value={scenario.additional_funds}
                  onChange={(v) => update("additional_funds", v)}
                  placeholder="0"
                  testid="scenario-additional-funds"
                />
              </Field>
            )}
          </>
        )}

        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          data-testid="scenario-continue"
          className="w-full bg-brand-navy text-white rounded-xl py-4 font-bold text-base font-body inline-flex items-center justify-center gap-2 hover:bg-brand-navy-hover transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          Continue
          <ArrowRight size={18} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-[0.14em] text-brand-navy mb-2 font-body">
        {label}
      </span>
      {hint && (
        <span className="block text-[11px] text-slate-500 mb-2 font-body">{hint}</span>
      )}
      {children}
    </label>
  );
}

function MoneyInput({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-display">$</span>
      <input
        type="number"
        inputMode="numeric"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full pl-8 pr-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-display tabular-nums bg-white text-brand-navy placeholder:text-slate-400"
      />
    </div>
  );
}

function PercentInput({ value, onChange, placeholder, testid }) {
  // Same look as MoneyInput but with a trailing % adornment. Used for
  // rate inputs in the Switch-to-Fixed scenario form (current variable
  // rate + lender-offered fixed rate).
  return (
    <div className="relative">
      <input
        type="number"
        inputMode="decimal"
        step="0.01"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full pl-4 pr-9 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-display tabular-nums bg-white text-brand-navy placeholder:text-slate-400"
      />
      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-display">%</span>
    </div>
  );
}

function SegmentedButtons({ value, onChange, options, testid }) {
  const cols = options.length === 2 ? "grid-cols-2" : options.length === 4 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3";
  return (
    <div className={`grid ${cols} gap-2`} data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          data-testid={`${testid}-${o.value}`}
          className={`px-2.5 py-2.5 rounded-xl border-2 text-xs font-bold font-body transition-all ${
            value === o.value
              ? "border-brand-navy bg-brand-navy text-white"
              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function MathCard({ children }) {
  return (
    <div
      className="bg-brand-navy text-white p-5 rounded-2xl shadow-inner animate-fade-up"
      aria-live="polite"
      data-testid="scenario-math-card"
    >
      {children}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-[13px] text-white/70 font-body">{label}</span>
      <span className={`font-display tabular-nums ${bold ? "text-xl font-bold" : "text-sm"}`}>
        {value}
      </span>
    </div>
  );
}

function Divider() {
  return <div className="my-1.5 border-t border-white/15" />;
}

// ---------- Contact gate -------------------------------------------------
function ContactGate({ intent, contact, onChange, error, submitting, onSubmit }) {
  const isSwitch = intent === "switch_to_fixed";
  const update = (k, v) => onChange({ ...contact, [k]: v });
  return (
    <div className="px-5 sm:px-8 py-7 sm:py-9 animate-fade-in">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-navy font-display mb-7">
        A bit about you.
      </h2>

      <div className="space-y-4">
        {isSwitch ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First name">
              <input type="text" value={contact.first_name || ""} onChange={(e) => onChange({ ...contact, first_name: e.target.value, name: `${e.target.value} ${contact.last_name || ""}`.trim() })} placeholder="Jane" data-testid="contact-first-name" className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400" />
            </Field>
            <Field label="Last name">
              <input type="text" value={contact.last_name || ""} onChange={(e) => onChange({ ...contact, last_name: e.target.value, name: `${contact.first_name || ""} ${e.target.value}`.trim() })} placeholder="Smith" data-testid="contact-last-name" className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400" />
            </Field>
          </div>
        ) : (
        <Field label="Full name">
          <input
            type="text"
            value={contact.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Jane Smith"
            data-testid="contact-name"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400"
          />
        </Field>
        )}
        <Field label="Email">
          <input
            type="email"
            inputMode="email"
            value={contact.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="jane@example.com"
            data-testid="contact-email"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400"
          />
          {contact.email && checkEmail(contact.email).suggestion && (
            <button type="button" className="mt-1 text-xs text-[#ff3356] underline font-body" data-testid="contact-email-suggestion" onClick={() => update("email", checkEmail(contact.email).suggestion)}>
              {checkEmail(contact.email).error} Click to fix.
            </button>
          )}
        </Field>
        <Field label="Phone">
          <input
            type="tel"
            inputMode="tel"
            required
            value={contact.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="403 555 1234"
            data-testid="contact-phone"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20 outline-none font-body bg-white text-brand-navy placeholder:text-slate-400"
          />
        </Field>
        <label className="hm-callback flex items-start gap-3 cursor-pointer select-none" data-testid="contact-callback-label">
          <input
            type="checkbox"
            checked={!!contact.wantsCall}
            onChange={(e) => update("wantsCall", e.target.checked)}
            data-testid="contact-callback"
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 accent-[#ff3356]"
          />
          <span className="font-body">
            <span className="block text-sm font-bold text-brand-navy">I'd like a call back about these rates.</span>
            <span className="block text-[11px] text-slate-500">We'll contact you about your options.</span>
          </span>
        </label>

        {error && (
          <div className="text-sm text-signal-up font-body" data-testid="contact-error">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          data-testid="contact-submit"
          className="w-full bg-brand-red text-white rounded-xl py-4 font-bold text-base font-body inline-flex items-center justify-center gap-2 hover:bg-brand-red-hover transition-colors disabled:opacity-70"
        >
          {submitting ? "Matching lenders…" : "Show My Rates"}
          {!submitting && <ArrowRight size={18} strokeWidth={2.5} />}
        </button>

        <div className="flex items-center justify-center gap-2 text-[11px] text-slate-400 font-body">
          <Lock size={12} />
          <span>Encrypted submission. We will never sell your details.</span>
        </div>
      </div>
    </div>
  );
}

// ---------- Reveal -------------------------------------------------------
function RevealResults({ matches, loanAmount, onInquire }) {
  const [showAll, setShowAll] = useState(false);
  if (!matches || matches.length === 0) {
    return (
      <div className="px-5 sm:px-8 py-9 text-center animate-fade-in">
        <h2 className="text-xl font-bold text-brand-navy font-display mb-3">
          No live matches for this scenario right now.
        </h2>
        <p className="text-sm text-slate-500 font-body mb-6">
          Our team will reach out within 1 business hour with hand-picked options.
        </p>
      </div>
    );
  }
  const PRIMARY_COUNT = 5;
  const visible = showAll ? matches : matches.slice(0, PRIMARY_COUNT);
  const remaining = Math.max(0, matches.length - PRIMARY_COUNT);

  return (
    <div className="px-5 sm:px-8 py-7 sm:py-9 animate-fade-in">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-navy font-display mb-2">
        Your top {visible.length} {visible.length === 1 ? "match" : "matches"}
      </h2>
      <p className="text-sm sm:text-base text-slate-500 font-body mb-6">
        Sorted lowest first · Mortgage of {fmtMoney(loanAmount)}
      </p>

      <div className="space-y-4" data-testid="reveal-list">
        {visible.map((m, i) => (
          <LenderResultCard
            key={m.lender_id}
            match={m}
            top={i === 0}
            onInquire={() => onInquire(m)}
            delay={i * 80}
          />
        ))}
      </div>

      {remaining > 0 && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          data-testid="reveal-show-more"
          className="mt-5 w-full bg-white border-2 border-slate-200 hover:border-brand-navy text-brand-navy rounded-xl py-3.5 font-bold text-sm font-body inline-flex items-center justify-center gap-2 transition-colors"
        >
          Show me {remaining} more {remaining === 1 ? "lender" : "lenders"}
          <ArrowRight size={16} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}


// ---------- Final --------------------------------------------------------

// Brand-side single source of truth for the Hello Mortgage application
// link. Mirrors `application_link` in `drip_engine._DEFAULT_DRIP_TOKENS`.

// Calendly booking link — broker confirmed Apr 2026. Prefill query-params
// follow Calendly's documented schema:
// https://help.calendly.com/hc/en-us/articles/360020052833
//
// We deliberately open this in a NEW TAB rather than using Calendly's
// `initPopupWidget` JS. The popup widget injects calendly.com as a
// third-party iframe, which gets killed by privacy extensions (uBlock,
// Privacy Badger, Ghostery), strict Safari/Firefox tracking-protection,
// AND collapses to zero-width on mobile. We saw three reported failures
// in production where visitors got the grey backdrop + close X but no
// calendar inside. Direct top-level navigation to calendly.com is what
// every modern SaaS (Ratehub, HubSpot, Intercom) does for exactly these
// reasons — Calendly's standalone page is fully responsive, has no
// third-party iframe issues, and renders identically for every user.
const CALENDLY_BASE_URL = "https://calendly.com/mattbroomhall/consultation";
const CALENDLY_PARAMS = {
  hide_event_type_details: 1,
  hide_gdpr_banner: 1,
  primary_color: "ff3356",
};

function buildCalendlyUrl(contact) {
  const params = new URLSearchParams();
  Object.entries(CALENDLY_PARAMS).forEach(([k, v]) => params.set(k, String(v)));
  if (contact?.name) params.set("name", contact.name);
  if (contact?.email) params.set("email", contact.email);
  return `${CALENDLY_BASE_URL}?${params.toString()}`;
}

function FinalActions({ selectedLender, contact, onAction, onClose }) {
  const [done, setDone] = useState({});

  const recordAction = async (action) => {
    try { await onAction(action); } catch (_e) { /* best-effort */ }
    setDone((d) => ({ ...d, [action]: true }));
  };

  const openExternal = (url) => {
    // Open in a new tab synchronously inside the user-gesture handler
    // so popup blockers don't fire. If the popup IS blocked, fall back
    // to a top-level navigation that escapes any iframe context — the
    // rate widget is sometimes embedded as an iframe on hellomortgage.ca,
    // and same-frame navigation there would leave the visitor stuck
    // inside the embed.
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      try { window.top.location.href = url; }
      catch (_e) { window.location.href = url; }
    }
  };

  const handleBookCall = () => {
    openExternal(buildCalendlyUrl(contact));
    recordAction("book_call");
  };

  const handleRateAlerts = () => {
    recordAction("rate_alerts");
  };

  return (
    <div className="px-5 sm:px-8 py-9 animate-fade-in text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-signal-stable-bg text-signal-stable mb-5">
        <CheckCircle2 size={32} strokeWidth={2.5} />
      </div>
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-navy font-display mb-2">
        You're locked in.
      </h2>
      <p className="text-sm sm:text-base text-slate-500 font-body mb-7 max-w-md mx-auto">
        {selectedLender ? (
          <>We've flagged <b className="text-brand-navy">{selectedLender}</b> for follow-up. The Hello Mortgage team will reach out within 1 business hour.</>
        ) : (
          <>The Hello Mortgage team will reach out within 1 business hour to lock in the right strategy.</>
        )}
      </p>

      <div className="space-y-3 max-w-md mx-auto text-left">
        <FinalAction
          icon={CalendarCheck}
          title="Book a Rate Strategy Call"
          subtitle="15 minutes with the Hello Mortgage team"
          done={done.book_call}
          doneLabel="Calendar opened in a new tab"
          onClick={handleBookCall}
          primary
          testid="final-book-call"
        />
        <FinalAction
          icon={Bell}
          title="Send Me Rate Alerts"
          subtitle="Notify me when rates change"
          done={done.rate_alerts}
          doneLabel="You're on the list"
          onClick={handleRateAlerts}
          testid="final-rate-alerts"
        />
      </div>

      <button
        type="button"
        onClick={onClose}
        data-testid="final-close"
        className="mt-7 text-sm text-slate-400 hover:text-brand-navy font-body underline-offset-4 hover:underline"
      >
        Close window
      </button>
    </div>
  );
}

function FinalAction({ icon: Icon, title, subtitle, done, doneLabel, onClick, primary, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={done}
      data-testid={testid}
      className={`w-full text-left flex items-center gap-4 px-4 py-4 rounded-xl border-2 transition-all ${
        done
          ? "border-signal-stable bg-signal-stable-bg cursor-default"
          : primary
          ? "border-brand-red bg-brand-red text-white hover:bg-brand-red-hover"
          : "border-slate-200 bg-white text-brand-navy hover:border-brand-navy"
      }`}
    >
      <span
        className={`inline-flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${
          done ? "bg-signal-stable text-white" : primary ? "bg-white/20 text-white" : "bg-brand-navy text-white"
        }`}
      >
        {done ? <CheckCircle2 size={18} /> : <Icon size={18} strokeWidth={2.5} />}
      </span>
      <div className="flex-1 min-w-0">
        <div className={`font-bold text-sm font-body ${primary && !done ? "text-white" : ""}`}>
          {done ? (doneLabel || "Done") : title}
        </div>
        <div className={`text-xs mt-0.5 font-body ${done ? "text-signal-stable" : primary ? "text-white/80" : "text-slate-500"}`}>
          {done ? "Tap again anytime — we logged it." : subtitle}
        </div>
      </div>
      {!done && <ArrowRight size={16} strokeWidth={2.5} />}
    </button>
  );
}

// ---------- Match payload builder ---------------------------------------
function buildMatchPayload(leadId, intent, scenario) {
  const isBuying = intent?.startsWith("buying");
  const isRefi = intent === "refinance";
  const occMap = {
    owner: "Owner-occupied",
    "second-home": "Second home",
    rental: "Rental",
  };
  return {
    lead_id: leadId,
    intent,
    province: "AB",
    occupancy: occMap[scenario.property_use] || "Owner-occupied",
    property_type: "Detached",
    term: scenario.term_pref || "5yr fixed",
    amortization_years: parseInt(scenario.amortization_years || 25, 10),
    purchase_price: isBuying ? parseFloat(scenario.purchase_price) || 0 : 0,
    down_payment: isBuying ? parseFloat(scenario.down_payment) || 0 : 0,
    property_value: !isBuying ? parseFloat(scenario.property_value) || 0 : 0,
    mortgage_balance: !isBuying ? parseFloat(scenario.mortgage_balance) || 0 : 0,
    additional_funds: isRefi ? parseFloat(scenario.additional_funds) || 0 : 0,
  };
}
